/**
 * PlayerSoundToggle
 *
 * Mute/unmute button in the header — and, while the Narrator is speaking, a
 * transport.
 *
 * It grows rather than sitting next to a second control, because a stop button
 * that is always visible is a stop button that is dead most of the time. There
 * is nothing to control until something is talking, so the affordance appears
 * exactly when it becomes true and collapses when it stops being true.
 */

import { useAudioStore } from '../stores/audioStore';
import { Icon } from './Icon';

const GOLD = '#d4af37';

export function PlayerSoundToggle() {
  const isMuted = useAudioStore((s) => s.isMuted);
  const setMuted = useAudioStore((s) => s.setMuted);
  const currentMood = useAudioStore((s) => s.currentMood);
  const activeLayers = useAudioStore((s) => s.activeLayers);
  const isSpeaking = useAudioStore((s) => s.isSpeaking);
  const voiceEnabled = useAudioStore((s) => s.voiceEnabled);
  const lastSpokenText = useAudioStore((s) => s.lastSpokenText);

  const hasAudio = currentMood || activeLayers.length > 0;
  const active = !isMuted && hasAudio;
  const iconColor = isMuted ? 'rgba(192,192,192,0.35)' : active ? GOLD : 'rgba(192,192,192,0.5)';

  const stop = () => {
    void import('../audio/voice').then(({ stopSpeaking }) => stopSpeaking());
  };
  const replay = () => {
    void import('../audio/voice').then(({ replayLast }) => replayLast());
  };

  // --- Speaking: the button becomes a transport --------------------------
  if (isSpeaking) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 28,
          padding: '0 10px 0 12px',
          background: 'rgba(212,175,55,0.08)',
          border: `1px solid ${GOLD}55`,
          borderRadius: 14,
          boxShadow: `0 0 10px ${GOLD}25`,
        }}
        title="The Narrator is speaking"
      >
        {/* Three bars that rise and fall — reads as speech without needing a
            label, and does not imply a progress position we do not know. */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 12 }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                width: 2,
                background: GOLD,
                borderRadius: 1,
                animation: `narrator-bar 900ms ease-in-out ${i * 150}ms infinite`,
              }}
            />
          ))}
        </div>

        <button
          onClick={stop}
          title="Stop the Narrator"
          style={{
            width: 18,
            height: 18,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: GOLD,
          }}
        >
          {/* A square, because this genuinely stops rather than pausing —
              there is no resume from the middle of a synthesised sentence. */}
          <span style={{ width: 9, height: 9, background: GOLD, borderRadius: 1, display: 'block' }} />
        </button>
      </div>
    );
  }

  // --- Idle, but there is something to replay ----------------------------
  const canReplay = voiceEnabled && !!lastSpokenText;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <button
        onClick={() => setMuted(!isMuted)}
        title={isMuted ? 'Unmute audio' : 'Mute audio'}
        style={{
          width: 28,
          height: 28,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: `1px solid ${active ? `${GOLD}40` : 'rgba(192,192,192,0.2)'}`,
          borderRadius: '50%',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: active ? `0 0 8px ${GOLD}30` : 'none',
          opacity: isMuted ? 0.5 : 1,
        }}
      >
        <Icon name={isMuted ? 'pause' : 'play'} size={14} color={iconColor} />
      </button>

      {canReplay && (
        <button
          onClick={replay}
          title="Read the last narration again"
          style={{
            width: 22,
            height: 22,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            opacity: 0.55,
            transition: 'opacity 0.2s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.55'; }}
        >
          <Icon name="refresh-cw" size={12} color={GOLD} />
        </button>
      )}
    </div>
  );
}
